/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'react-loading-overlay-ts';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Button, Card, CardActions, CardContent, List, ListSubheader, MenuItem, Paper, TextField, Typography } from '@mui/material';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import MapIcon from '@mui/icons-material/Map';
import WarningIcon from '@mui/icons-material/Warning';

import { ViewHeader } from '@components/ViewHeader';
import { GrowerFieldsModal } from '@components/GrowerFieldsModal';
import { useGetGrower } from '@hooks/useGetGrower';
import { useAppTheme } from '@hooks/useAppTheme';
import { useGetClientUsers } from '@hooks/useGetClientUsers';
import { AppDispatch, RootState } from '@state-mgmt/store';
import { styles } from './styles';
import { groupTrialsByYear, TrialsGropedByYear } from '@utils/groupTrialsByYear';
import { Trial } from '@mytypes/trial';
import { userPermissions } from '@hooks/useUserPermissions';
import { useUserPerms } from '@utils/useUserPerms';
import { setEnrollmentGrowerFilter, setEnrollmentShowBy, setTrialId } from '@state-mgmt/slices/appSlice';
import { setCurrentProtocol } from '@state-mgmt/slices/protocol-slice';
import { useGetProtocols } from '@hooks/useGetProtocols';
import { useGetFields } from '@hooks/useGetFields';
import { useGetTrialGrowerMembers } from '@hooks/useGetTrialGrowerMembers';
import { useGetTrialProducts } from '@hooks/useGetTrialProducts';
import { useGetTrialTags } from '@hooks/useGetTrialTags';
import { setTrialTags } from '@state-mgmt/slices/trial-slice';

const Enrollment = () => {
  const { t } = useTranslation();
  const theme = useAppTheme({});
  const navigate = useNavigate();
  const { clientId, trialId, enrollmentShowBy, enrollmentGrowerFilter } = useSelector((state: RootState) => state.app);
  const { fields } = useSelector((state: RootState) => state.trial);
  const { currentProtocol } = useSelector((state: RootState) => state.protocol);
  const { clients } = useSelector((rootState: RootState) => rootState.client);

  const [openGrowerFieldsModal, setOpenGrowerFieldsModal] = useState<boolean>(false);
  const getGrower = useGetGrower();
  const [data, setData] = useState<any[]>();
  const [getClientMembersState, getClientMembers] = useGetClientUsers();
  const [growerId, setGrowerId] = useState<string | undefined>();

  const { trials } = useSelector((state: RootState) => state.trial);
  const client = useMemo(() => {
    return clients.find(c => c.id === clientId);
  }, [clients, clientId]);

  const clientFieldAccessManagement = client?.field_access_management || false;
  const dispatch = useDispatch<AppDispatch>();

  const { getUserPermissionsByTrialId } = useUserPerms();
  const setShowBy = (showBy: string) => {
    dispatch(setEnrollmentShowBy(showBy));
  };
  const setSelectedGrower = (grower: { id: string; label: string } | null) => {
    dispatch(setEnrollmentGrowerFilter(grower));
  };

  const trialOptions = useMemo(() => {
    const options = [] as ReactNode[];
    const filtered = trials.filter(t => {
      const perms = getUserPermissionsByTrialId(t.id);
      return perms.includes(userPermissions.ENROLL) || perms.includes(userPermissions.SUPER_ADMIN);
    });
    const trialsGroupedByYear = groupTrialsByYear(filtered);

    const trialYears = Object.keys(trialsGroupedByYear)
      .sort((a: string, b: string) => a.localeCompare(b))
      .reverse();

    trialYears.forEach(year => {
      options.push(<ListSubheader key={year}>{year}</ListSubheader>);

      trialsGroupedByYear[year as keyof TrialsGropedByYear]
        .sort((a: Trial, b: Trial) => a.name.localeCompare(b.name))
        .forEach((trial: Trial) => {
          options.push(
            <MenuItem key={trial.id} value={trial.id}>
              {trial.name}
            </MenuItem>
          );
        });
    });
    return options;
  }, [trials, getUserPermissionsByTrialId]);

  useEffect(() => {
    if (!clientId) return;
    getClientMembers(clientId, trialId || null);
  }, [clientId, trialId]);

  const getGrowerStatus = useCallback(
    (growerId: string) => {
      let growerProtocols = getGrower(growerId)?.protocol_ids;
      if (growerProtocols?.length) {
        if (currentProtocol) {
          growerProtocols = growerProtocols?.filter(gp => gp.protocol_id === currentProtocol);
        }

        if (growerProtocols.every(gp => gp.dropout)) {
          return { status: 're-enroll' };
        }

        let fieldsEnrolled = fields.filter(field => field.owner_id === growerId);
        if (fieldsEnrolled.length) {
          return { status: 'enrolled', fieldsEnrolled: fieldsEnrolled.length };
        } else {
          return { status: 'enroll-not-complete' };
        }
      } else {
        return { status: 'enroll' };
      }
    },
    [getGrower, fields]
  );

  useEffect(() => {
    if (getClientMembersState.data) {
      const processData = getClientMembersState.data.map(row => {
        const { status, fieldsEnrolled } = getGrowerStatus(row.user_id);
        if (enrollmentShowBy === 'enrolled' && ['enrolled', 'enroll-not-complete'].includes(status)) {
          return { ...row, fullname: `${row.first} ${row.last}`, status, fieldsEnrolled };
        }
        if (enrollmentShowBy === 'unenrolled' && ['enroll', 're-enroll'].includes(status)) {
          return { ...row, fullname: `${row.first} ${row.last}`, status };
        }
        if (enrollmentShowBy === 'all') {
          return { ...row, fullname: `${row.first} ${row.last}`, status };
        }
      });
      processData.sort((a: any, b: any) => a.fullname.localeCompare(b.fullname));
      setData(processData.filter((row: any) => row));
    }
  }, [getClientMembersState.isSuccess, enrollmentShowBy, getGrowerStatus]);

  const [getProtocolsData, getProtocols] = useGetProtocols();
  const [getFieldsData, getFields] = useGetFields();
  const [getMembersData, getMembers] = useGetTrialGrowerMembers();
  const [getTrialsProductsData, getTrialsProducts] = useGetTrialProducts();
  const [getTrialTagsData, getTrialTags] = useGetTrialTags();

  const onShowByChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as string;
    setShowBy(value);
  };
  const onTrialChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as string;
    dispatch(setTrialId(value));
    localStorage.setItem('trialId', value);
    dispatch(setCurrentProtocol(undefined));
    setShowBy('enrolled');
    setSelectedGrower(null);
  };

  useEffect(() => {
    if (trialId) {
      getProtocols(trialId);
      getFields(trialId);
      getMembers(trialId);
      getTrialsProducts(trialId);
    }
  }, [trialId]);

  useEffect(() => {
    if (trialId && clientFieldAccessManagement) {
      getTrialTags(trialId);
    } else {
      dispatch(setTrialTags(null));
    }
  }, [trialId, clientFieldAccessManagement]);

  return (
    <Paper css={styles.container(theme)}>
      <ViewHeader title={t('enrollment.title')} hideGoBackBtn={true} showRefreshBtn={true} />
      <div css={styles.scrollableArea(theme)}>
        {/* Trial Filter */}
        <TextField sx={{ marginTop: 1 }} select fullWidth label={t('general.trial')} value={trialId || ''} onChange={onTrialChange}>
          {trialOptions}
        </TextField>

        {/* Status Filter */}
        <TextField select fullWidth label={t('enrollment.status-filter-label')} value={enrollmentShowBy} onChange={onShowByChange} css={styles.filter(theme)}>
          <MenuItem key="Enrolled" value="enrolled">
            {t('enrollment.enrolled-status-label')}
          </MenuItem>
          <MenuItem key="unenrolled" value="unenrolled">
            {t('enrollment.unenrolled-status-label')}
          </MenuItem>
          <MenuItem key="all" value="all">
            {t('general.all')}
          </MenuItem>
        </TextField>

        {/* Grower Filter */}
        <Autocomplete
          value={enrollmentGrowerFilter}
          options={data?.map(({ user_id, first, last }) => ({ id: user_id, label: first + ' ' + last })) || []}
          renderInput={params => <TextField {...params} label={t('enrollment.grower-filter-label')} />}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.label}
            </li>
          )}
          onChange={(event: any, newValue) => setSelectedGrower(newValue ? newValue : null)}
          isOptionEqualToValue={option => option.id === enrollmentGrowerFilter?.id}
          css={styles.growerFilter(theme)}
        />

        <LoadingOverlay
          spinner
          active={
            getClientMembersState.isLoading ||
            getProtocolsData.isLoading ||
            getFieldsData.isLoading ||
            getMembersData.isLoading ||
            getTrialsProductsData.isLoading ||
            getTrialTagsData.isLoading
          }
          text={t('enrollment.loading-message')}
          styles={{ wrapper: styles.listLoadingOverlayContent }}
        >
          <List>
            {data?.map(({ user_id, first, last, email, status, fieldsEnrolled }) => {
              return !enrollmentGrowerFilter || (enrollmentGrowerFilter && enrollmentGrowerFilter.id === user_id) ? (
                <Card key={user_id} elevation={0} css={styles.userCard(theme)}>
                  <div css={styles.cardHeader(theme)}>
                    <Typography color="text.primary">{first + ' ' + last}</Typography>
                  </div>

                  <CardContent css={styles.cardContent}>
                    <Typography color="text.secondary" css={styles.email}>
                      {email}
                    </Typography>
                  </CardContent>
                  <CardActions css={styles.cardActions}>
                    {status === 'enroll' && (
                      <Button size="small" onClick={() => navigate(`/enrollment/new?grower_id=${user_id}`)}>
                        {t('enrollment.enroll-button')}
                      </Button>
                    )}
                    {status === 're-enroll' && (
                      <Button
                        size="small"
                        onClick={() => {
                          setGrowerId(user_id);
                          setOpenGrowerFieldsModal(true);
                        }}
                      >
                        {t('enrollment.re-enroll-button')}
                      </Button>
                    )}
                    {status === 'enrolled' && (
                      <Button
                        size="small"
                        startIcon={<MapIcon />}
                        onClick={() => {
                          setGrowerId(user_id);
                          setOpenGrowerFieldsModal(true);
                        }}
                      >
                        {t('enrollment.view-enrolled-fields-button', { fields: fieldsEnrolled })}
                      </Button>
                    )}
                    {status === 'enroll-not-complete' && (
                      <Button
                        size="small"
                        startIcon={<WarningIcon />}
                        onClick={() => {
                          setGrowerId(user_id);
                          setOpenGrowerFieldsModal(true);
                        }}
                      >
                        {t('enrollment.enrolled-not-complete-status-button')}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              ) : (
                <></>
              );
            })}
          </List>
        </LoadingOverlay>
      </div>
      <Button css={styles.enrollBtn(theme)} variant="outlined" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => navigate('/enrollment/new')}>
        {t('enrollment.enroll-a-new-grower-button')}
      </Button>

      <GrowerFieldsModal
        growerId={growerId}
        open={openGrowerFieldsModal}
        onClose={needToRefresh => {
          setOpenGrowerFieldsModal(false);
          if (needToRefresh) {
            navigate('/splash?redirectTo=/enrollment');
          }
        }}
      />
    </Paper>
  );
};

export default Enrollment;
